import React, { useState, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { ContentText } from '../Common/ContentText';
import styles from './DomainCardSelectorModal.module.css';

interface DomainCard {
  id: string;
  name: string;
  domain: string;
  type: string;
  level: number;
  recallCost: number;
  description: string;
  tags?: string[];
}

interface DomainInfo {
  id: string;
  name: string;
  classes?: string[];
}

interface DomainCardSelectorModalProps {
  isOpen: boolean;
  characterClass: string;
  characterLevel: number;
  ownedCardIds?: string[]; // Array of card IDs the character already owns
  selectedCardIds?: string[]; // Array of card IDs that are already selected (for resuming selection)
  onConfirm: (cardIds: string[]) => void;
  onCancel: () => void;
  selectCount?: number; // Number of cards to select (default 1)
  title?: string;
  subtitle?: string;
}

/**
 * Reusable Domain Card Selector Modal
 * Supports selecting 1 or more domain cards
 * Used by LevelUpWizard (selectCount=1) and CharacterCreation (selectCount=2)
 */
export const DomainCardSelectorModal: React.FC<DomainCardSelectorModalProps> = ({
  isOpen,
  characterClass,
  characterLevel,
  ownedCardIds = [],
  selectedCardIds: preselectedCardIds = [],
  onConfirm,
  onCancel,
  selectCount = 1,
  title = 'Select Domain Card',
  subtitle = 'Choose a domain card from your class\'s available domains.'
}) => {
  const [allCards, setAllCards] = useState<DomainCard[]>([]);
  const [domains, setDomains] = useState<DomainInfo[]>([]);
  const [selectedDomain, setSelectedDomain] = useState<string>('all');
  const [selectedCardIds, setSelectedCardIds] = useState<Set<string>>(new Set());
  const [expandedCardId, setExpandedCardId] = useState<string | null>(null);

  // Load domain cards and domain info
  useEffect(() => {
    const loadData = async () => {
      try {
        const [cardsData, domainsData] = await Promise.all([
          import('../../data/domain-cards.json'),
          import('../../data/domains.json')
        ]);
        setAllCards(cardsData.domainCards);
        setDomains(domainsData.domains);
      } catch (error) {
        console.error('Failed to load domain card data:', error);
      }
    };
    loadData();
  }, []);

  // Initialize selected cards when modal opens with preselected cards
  useEffect(() => {
    if (isOpen && preselectedCardIds.length > 0) {
      setSelectedCardIds(new Set(preselectedCardIds));
    } else if (isOpen) {
      // Clear selection when modal opens (if no preselected cards)
      setSelectedCardIds(new Set());
      setSelectedDomain('all');
      setExpandedCardId(null);
    }
  }, [isOpen, preselectedCardIds.join(',')]);

  // Get character's available domains based on class
  const availableDomains = useMemo(() => {
    if (!characterClass || !domains.length) return [];

    // Case-insensitive class name matching
    const classLower = characterClass.toLowerCase();
    return domains.filter(domain =>
      domain.classes?.some(cls => cls.toLowerCase() === classLower)
    );
  }, [characterClass, domains]);

  // Filter cards by level and domain, excluding owned cards
  const availableCards = useMemo(() => {
    if (!allCards.length) return [];

    const domainIds = availableDomains.map(d => d.id.toLowerCase());
    const ownedSet = new Set(ownedCardIds.map(id => id.toLowerCase()));

    return allCards.filter(card =>
      card.level <= characterLevel &&
      domainIds.includes(card.domain.toLowerCase()) &&
      !ownedSet.has(card.id.toLowerCase())
    );
  }, [allCards, availableDomains, characterLevel, ownedCardIds]);

  // Filter by selected domain
  const filteredCards = useMemo(() => {
    if (selectedDomain === 'all') return availableCards;
    return availableCards.filter(card =>
      card.domain.toLowerCase() === selectedDomain.toLowerCase()
    );
  }, [availableCards, selectedDomain]);

  // Group cards by domain for display
  const cardsByDomain = useMemo(() => {
    const grouped: Record<string, DomainCard[]> = {};
    filteredCards.forEach(card => {
      const domainName = card.domain;
      if (!grouped[domainName]) {
        grouped[domainName] = [];
      }
      grouped[domainName].push(card);
    });
    return grouped;
  }, [filteredCards]);

  const handleToggleCardSelection = (cardId: string) => {
    const newSelection = new Set(selectedCardIds);

    if (newSelection.has(cardId)) {
      // Deselect
      newSelection.delete(cardId);
    } else {
      // Select only if under limit
      if (newSelection.size < selectCount) {
        newSelection.add(cardId);
      }
    }

    setSelectedCardIds(newSelection);
  };

  const handleToggleExpand = (cardId: string) => {
    setExpandedCardId(expandedCardId === cardId ? null : cardId);
  };

  const handleConfirm = () => {
    if (selectedCardIds.size === selectCount) {
      onConfirm(Array.from(selectedCardIds));
    }
  };

  const handleCancel = () => {
    onCancel();
  };

  if (!isOpen) return null;

  const isMaxSelected = selectedCardIds.size >= selectCount;
  const selectedCount = selectedCardIds.size;

  const modalContent = (
    <div className={styles.modalOverlay} onClick={handleCancel}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h3 className={styles.title}>{title}</h3>
          <button
            type="button"
            onClick={handleCancel}
            className={styles.closeButton}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <p className={styles.subtitle}>{subtitle}</p>

        {selectedCount > 0 && (
          <div className={styles.progressSection}>
            <div className={styles.progressCounter}>
              Selected: {selectedCount} / {selectCount} card{selectCount !== 1 ? 's' : ''}
            </div>
          </div>
        )}

        <div className={styles.filterSection}>
          <label className={styles.filterLabel}>Filter by Domain:</label>
          <select
            value={selectedDomain}
            onChange={(e) => setSelectedDomain(e.target.value)}
            className={styles.domainFilter}
          >
            <option value="all">All Domains</option>
            {availableDomains.map(domain => (
              <option key={domain.id} value={domain.id}>
                {domain.name}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.cardsContainer}>
          {Object.keys(cardsByDomain).length === 0 ? (
            <div className={styles.noCards}>
              No domain cards available for your class and level.
            </div>
          ) : (
            Object.entries(cardsByDomain).map(([domainName, cards]) => (
              <div key={domainName} className={styles.domainGroup}>
                <h4 className={styles.domainHeader}>
                  {domainName.toUpperCase()} DOMAIN
                </h4>
                <div className={styles.cardsList}>
                  {cards
                    .sort((a, b) => a.level - b.level)
                    .map(card => {
                      const isSelected = selectedCardIds.has(card.id);
                      const isExpanded = expandedCardId === card.id;
                      const isDisabled = isMaxSelected && !isSelected;

                      return (
                        <div
                          key={card.id}
                          className={`${styles.card} ${isSelected ? styles.cardSelected : ''} ${isDisabled ? styles.cardDisabled : ''}`}
                        >
                          <div
                            className={styles.cardCompact}
                            onClick={() => !isDisabled && handleToggleCardSelection(card.id)}
                          >
                            <div className={styles.cardInfo}>
                              <div className={styles.cardNameRow}>
                                <span className={styles.cardLevel}>Lv {card.level}</span>
                                <span className={styles.cardNameCompact}>{card.name}</span>
                              </div>
                              <div className={styles.cardMeta}>
                                <span className={styles.cardTypeBadge}>{card.type}</span>
                                <span className={styles.recallCost}>Recall: {card.recallCost}</span>
                              </div>
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleToggleExpand(card.id);
                              }}
                              className={styles.expandButton}
                              aria-label={`${isExpanded ? 'Hide' : 'Show'} details for ${card.name}`}
                            >
                              {isExpanded ? '▼' : '▶'}
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleToggleCardSelection(card.id);
                              }}
                              className={styles.selectCardButton}
                              disabled={isDisabled}
                              aria-label={isSelected ? `Deselect ${card.name}` : `Select ${card.name}`}
                            >
                              {isSelected ? '✓ Selected' : 'Select'}
                            </button>
                          </div>

                          {isExpanded && (
                            <div className={styles.cardExpanded}>
                              <ContentText
                                content={card.description}
                                className={styles.cardDescription}
                              />
                            </div>
                          )}
                        </div>
                      );
                    })}
                </div>
              </div>
            ))
          )}
        </div>

        <div className={styles.footer}>
          <button
            onClick={handleCancel}
            className={styles.cancelButton}
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            className={styles.confirmButton}
            disabled={selectedCardIds.size !== selectCount}
          >
            Confirm ({selectedCount}/{selectCount})
          </button>
        </div>
      </div>
    </div>
  );

  const modalRoot = document.getElementById('modal-root');
  return modalRoot ? ReactDOM.createPortal(modalContent, modalRoot) : null;
};
